/**
 * Quick view — opens a product preview in a modal without leaving the page.
 * Loads a dedicated `quick-view` section via the Section Rendering API and
 * reuses Dawn's <product-form> element for the actual add-to-cart, so the
 * cart drawer / checkout flow stays Dawn's tested code.
 */
if (!customElements.get('quick-view-opener')) {
  customElements.define(
    'quick-view-opener',
    class QuickViewOpener extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.dataset.productUrl) window.QuickView.open(this.dataset.productUrl);
        });
      }
    }
  );
}

window.QuickView = {
  modal: null,
  lastFocus: null,

  ensureModal() {
    if (this.modal) return this.modal;
    const m = document.createElement('div');
    m.className = 'quick-view-modal';
    m.setAttribute('aria-hidden', 'true');
    m.innerHTML =
      '<div class="quick-view-modal__overlay" data-qv-close></div>' +
      '<div class="quick-view-modal__dialog" role="dialog" aria-modal="true" aria-label="Product quick view">' +
      '<button class="quick-view-modal__close" type="button" data-qv-close aria-label="Close">' +
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
      '</button>' +
      '<div class="quick-view-modal__content" data-qv-content></div>' +
      '</div>';
    document.body.appendChild(m);
    m.addEventListener('click', (e) => {
      if (e.target.closest('[data-qv-close]')) this.close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal && this.modal.classList.contains('is-open')) this.close();
    });
    this.modal = m;
    return m;
  },

  async open(url) {
    this.lastFocus = document.activeElement;
    const m = this.ensureModal();
    const content = m.querySelector('[data-qv-content]');
    content.innerHTML = '<div class="quick-view-modal__loading">Loading…</div>';
    m.classList.add('is-open');
    m.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('quick-view-open');

    try {
      const sep = url.indexOf('?') === -1 ? '?' : '&';
      const res = await fetch(url + sep + 'section_id=quick-view');
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const section = doc.querySelector('.quick-view');
      if (!section) throw new Error('No quick-view content');
      content.innerHTML = section.outerHTML;
      this.init(content);
      const closeBtn = m.querySelector('.quick-view-modal__close');
      if (closeBtn) closeBtn.focus();
    } catch (e) {
      content.innerHTML =
        '<p class="quick-view-modal__error">Sorry, this product could not be loaded. ' +
        '<a href="' + url + '">View the full page</a>.</p>';
    }
  },

  close() {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('quick-view-open');
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  },

  init(root) {
    // Thumbnail gallery
    const main = root.querySelector('[data-qv-main-image]');
    root.querySelectorAll('[data-qv-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        if (main && thumb.dataset.full) {
          main.src = thumb.dataset.full;
          root.querySelectorAll('[data-qv-thumb]').forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        }
      });
    });

    // Variant selection
    const dataEl = root.querySelector('[data-qv-variants]');
    if (!dataEl) return;
    let variants = [];
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    const selects = Array.from(root.querySelectorAll('[data-qv-option]'));
    const idInput = root.querySelector('input[name="id"]');
    const priceEl = root.querySelector('[data-qv-price]');
    const submit = root.querySelector('[type="submit"]');
    const submitText = submit ? submit.querySelector('span') : null;

    const findVariant = () => {
      const chosen = selects.map((s) => s.value);
      return variants.find(
        (v) => v.options.length === chosen.length && v.options.every((o, i) => o === chosen[i])
      );
    };

    const update = () => {
      const v = selects.length ? findVariant() : variants[0];
      if (idInput && v) idInput.value = v.id;
      if (priceEl && v && v.priceFormatted) priceEl.innerHTML = v.priceFormatted;
      if (submit && submitText) {
        if (!v || !v.available) {
          submit.setAttribute('disabled', 'disabled');
          submit.setAttribute('aria-disabled', 'true');
          submitText.textContent = v ? 'Sold out' : 'Unavailable';
        } else {
          submit.removeAttribute('disabled');
          submit.setAttribute('aria-disabled', 'false');
          submitText.textContent = 'Add to cart';
        }
      }
    };

    selects.forEach((s) => s.addEventListener('change', update));
    update();
  },
};
